import type { QuoteDetail, QuoteItem, QuoteOption } from '@techgitter/contracts';
import { optionLineSellDisplay } from './quotePricing';

/**
 * Billing groups — marked items that share a `billingGroupId` bill as ONE
 * customer-facing line (name = group name, price = SUM of members, description
 * = the group description or an auto "Includes: …"). Ungrouped marked items
 * still bill on their own line.
 *
 * This module is the single frontend source of truth for resolving groups,
 * mirroring the backend's `_merge_billing_group_lines` — reused by the customer
 * preview AND the editors' billing-group panels so the maths can't drift.
 */

export const CUSTOM_DESKTOP_TYPE = 'new_desktop_custom';
/** Fallback line name when a group has no name and no member descriptions. */
export const BILLING_GROUP_DEFAULT_NAME = 'Package';

/** The marked item's selected option, or null. */
export const selectedOption = (item: QuoteItem): QuoteOption | null =>
  item.options.find((o) => o.id === item.selectedOptionId) ?? null;

/** True when any marked+selected item carries a billing group. */
export const hasBillingGroups = (items: QuoteItem[]): boolean =>
  items.some(
    (it) => it.markedForInvoice && it.selectedOptionId && it.billingGroupId,
  );

export interface BillingGroup {
  id: string;
  /** Stored group name (may be blank). */
  rawName: string;
  /** Stored group description override (null → auto Includes). */
  description: string | null;
  /** Member marked+selected items, in position order. */
  items: QuoteItem[];
  /** First member's position (drives group ordering among the lines). */
  position: number;
}

/**
 * Resolve the billing groups on a quote's items, in first-member position
 * order. Only marked items with a selected option and a `billingGroupId`
 * participate. Name/description are taken from the first member that has them.
 */
export const resolveBillingGroups = (items: QuoteItem[]): BillingGroup[] => {
  const byId = new Map<string, BillingGroup>();
  const order: string[] = [];
  const sorted = [...items].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  for (const it of sorted) {
    if (!it.markedForInvoice || !it.selectedOptionId || !it.billingGroupId) {
      continue;
    }
    const gid = it.billingGroupId;
    let g = byId.get(gid);
    if (!g) {
      g = {
        id: gid,
        rawName: (it.billingGroupName ?? '').trim(),
        description:
          it.billingGroupDescription != null ? it.billingGroupDescription : null,
        items: [],
        position: it.position ?? 0,
      };
      byId.set(gid, g);
      order.push(gid);
    }
    // First non-empty name/description among members wins (they're synced).
    if (!g.rawName && (it.billingGroupName ?? '').trim()) {
      g.rawName = (it.billingGroupName ?? '').trim();
    }
    if (
      (g.description == null || g.description.trim() === '') &&
      (it.billingGroupDescription ?? '').trim()
    ) {
      g.description = it.billingGroupDescription ?? null;
    }
    g.items.push(it);
  }
  return order.map((id) => byId.get(id)!);
};

/** Auto description "a • b • c" from a group's members' selected-option descriptions. */
export const autoIncludesText = (members: QuoteItem[]): string =>
  members
    .map((it) => (selectedOption(it)?.description ?? '').trim())
    .filter(Boolean)
    .join(' • ');

/** Customer-facing NAME for a group line (name → joined member descs → default). */
export const groupLineName = (group: BillingGroup): string => {
  if (group.rawName) return group.rawName;
  const descs = group.items
    .map((it) => (selectedOption(it)?.description ?? '').trim())
    .filter(Boolean);
  return descs.join(', ') || BILLING_GROUP_DEFAULT_NAME;
};

/** Customer-facing DESCRIPTION for a group line (override → auto Includes). */
export const groupLineDescription = (group: BillingGroup): string =>
  (group.description ?? '').trim() || autoIncludesText(group.items);

/** Summed customer line total for a group, in the requested GST display mode. */
export const groupLineTotal = (
  group: BillingGroup,
  mode: 'ex' | 'inc',
): number => {
  const sum = group.items.reduce((acc, it) => {
    const opt = selectedOption(it);
    return acc + (opt ? optionLineSellDisplay(opt, mode) : 0);
  }, 0);
  return Math.round(sum * 100) / 100;
};

/**
 * Effective consolidation for the CUSTOMER-facing render, mirroring the
 * backend: a Custom Desktop build is one consolidated line until any billing
 * group exists (then it splits into group + ungrouped lines); every other type
 * uses its stored `consolidated` flag. Billing groups only apply when NOT
 * consolidated.
 */
export const isEffectivelyConsolidated = (quote: QuoteDetail): boolean => {
  if (quote.type === CUSTOM_DESKTOP_TYPE) {
    return !hasBillingGroups(quote.items);
  }
  return quote.consolidated;
};

// ---------------------------------------------------------------------------
// Pure item transforms for the editor billing-group controls. All keep the
// group name/description in sync across every member item (denormalised).
// ---------------------------------------------------------------------------

export const newBillingGroupId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `bg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** The name/description an item should adopt when it joins an existing group. */
const groupMetaOf = (
  items: QuoteItem[],
  groupId: string,
): { name: string | null; description: string | null } => {
  const member = items.find(
    (it) => it.billingGroupId === groupId,
  );
  return {
    name: member?.billingGroupName ?? null,
    description: member?.billingGroupDescription ?? null,
  };
};

/**
 * Assign one item to a billing group. `groupId === null` → back to its own
 * line (clears the group fields). Otherwise the item joins that group and
 * adopts its current name/description.
 */
export const assignItemToGroup = (
  items: QuoteItem[],
  itemId: string,
  groupId: string | null,
): QuoteItem[] => {
  const meta = groupId ? groupMetaOf(items, groupId) : null;
  return items.map((it) => {
    if (it.id !== itemId) return it;
    if (!groupId) {
      return {
        ...it,
        billingGroupId: null,
        billingGroupName: null,
        billingGroupDescription: null,
      };
    }
    return {
      ...it,
      billingGroupId: groupId,
      billingGroupName: meta?.name ?? null,
      billingGroupDescription: meta?.description ?? null,
    };
  });
};

/** Rename a billing group — synced across every member. */
export const renameBillingGroup = (
  items: QuoteItem[],
  groupId: string,
  name: string,
): QuoteItem[] =>
  items.map((it) =>
    it.billingGroupId === groupId ? { ...it, billingGroupName: name } : it,
  );

/** Set a billing group's description override — synced across every member. */
export const setBillingGroupDescription = (
  items: QuoteItem[],
  groupId: string,
  description: string | null,
): QuoteItem[] =>
  items.map((it) =>
    it.billingGroupId === groupId
      ? { ...it, billingGroupDescription: description }
      : it,
  );

/** Disband a group — every member returns to its own line. */
export const ungroupBillingGroup = (
  items: QuoteItem[],
  groupId: string,
): QuoteItem[] =>
  items.map((it) =>
    it.billingGroupId === groupId
      ? {
          ...it,
          billingGroupId: null,
          billingGroupName: null,
          billingGroupDescription: null,
        }
      : it,
  );
